import type { V1DraftResponse } from '@aira/shared';
import type { ProgressGroup, ProgressStep } from '../api/ragBackend';
import { isWorkflowStorageExpired } from './workflowStorage';

const SNAPSHOT_PREFIX = 'aira-outlook-taskpane:';

export interface TaskpaneSnapshot {
  messageId: string;
  savedAt: number;
  response?: V1DraftResponse;
  editedDraft?: string;
  feedback?: 'like' | 'dislike' | null;
  progress?: ProgressStep[];
  groupedProgress?: ProgressGroup[];
  regenerateCount?: number;
  runId?: string;
  refinementOperationId?: string;
  dialogOpen?: boolean;
  dialogSeedId?: string;
}

type TaskpaneSnapshotUpdate = Omit<Partial<TaskpaneSnapshot>, 'messageId' | 'savedAt'>;

function snapshotKey(messageId: string): string {
  return `${SNAPSHOT_PREFIX}${messageId}`;
}

export function readTaskpaneSnapshot(messageId: string): TaskpaneSnapshot | null {
  try {
    const raw = window.localStorage.getItem(snapshotKey(messageId));
    if (!raw) return null;
    const snapshot = JSON.parse(raw) as TaskpaneSnapshot;
    if (
      snapshot.messageId !== messageId ||
      typeof snapshot.savedAt !== 'number' ||
      isWorkflowStorageExpired(snapshot.savedAt)
    ) {
      window.localStorage.removeItem(snapshotKey(messageId));
      return null;
    }
    if (
      snapshot.response &&
      (typeof snapshot.response.trace_id !== 'string' ||
        typeof snapshot.response.draft !== 'string')
    ) {
      return null;
    }
    if (
      snapshot.refinementOperationId !== undefined &&
      typeof snapshot.refinementOperationId !== 'string'
    ) {
      return null;
    }
    return snapshot;
  } catch {
    return null;
  }
}

export function updateTaskpaneSnapshot(
  messageId: string | undefined,
  update: TaskpaneSnapshotUpdate,
): void {
  if (!messageId) return;
  try {
    const existing = readTaskpaneSnapshot(messageId);
    const next: Partial<TaskpaneSnapshot> = { ...existing, ...update };
    // Callers use `undefined` as an explicit clear signal. Remove those keys
    // before persistence so stale optional state can never survive a merge.
    for (const key of Object.keys(update) as (keyof TaskpaneSnapshotUpdate)[]) {
      if (update[key] === undefined) delete next[key];
    }
    // Outlook may replace the taskpane webview while a dialog is open. Keep
    // this bounded snapshot in localStorage so the replacement can find the
    // durable dialog-session pointer; savedAt enforces the short retention.
    window.localStorage.setItem(
      snapshotKey(messageId),
      JSON.stringify({ ...next, messageId, savedAt: Date.now() }),
    );
  } catch {
    // Outlook can disable storage in restricted contexts. Runtime state still works.
  }
}

export function clearTaskpaneSnapshot(messageId: string | undefined): void {
  if (!messageId) return;
  try {
    window.localStorage.removeItem(snapshotKey(messageId));
  } catch {
    // Ignore restricted-storage hosts.
  }
}
